import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useLanguage } from '../LanguageContext';
import { Plus, Type, Image as ImageIcon, Trash2, Move, Maximize2 } from 'lucide-react';
import { getPolaroidLayout } from '../../utils/polaroidLayout';

// ── Google Fonts to load (English + Hebrew + script families that actually render) ────
const GOOGLE_FONTS_URL =
    'https://fonts.googleapis.com/css2?' +
    'family=Rubik:wght@400;700;900' +
    '&family=Heebo:wght@400;700;900' +
    '&family=Assistant:wght@400;700' +
    '&family=Frank+Ruhl+Libre:wght@500;700' +
    '&family=Suez+One' +
    '&family=Secular+One' +
    '&family=Varela+Round' +
    '&family=Playfair+Display:ital,wght@0,700;1,700' +
    '&family=Dancing+Script:wght@700' +
    '&family=Great+Vibes' +
    '&family=Lobster' +
    '&family=Pacifico' +
    '&family=Caveat:wght@700' +
    '&family=Bebas+Neue' +
    '&family=Oswald:wght@500;700' +
    '&family=Raleway:wght@700' +
    '&family=Montserrat:wght@700;900' +
    '&family=Poppins:wght@600;900' +
    '&display=swap';

// Grouped by family — each entry has the actual CSS font-family string so it applies cleanly.
const FONTS = [
    // Hebrew-friendly
    { value: '"Rubik", sans-serif',           label: 'Rubik · רגיל',         hebrew: true },
    { value: '"Heebo", sans-serif',           label: 'Heebo · נקי',          hebrew: true },
    { value: '"Assistant", sans-serif',       label: 'Assistant · עכשווי',   hebrew: true },
    { value: '"Frank Ruhl Libre", serif',     label: 'Frank Ruhl · אלגנטי',  hebrew: true },
    { value: '"Suez One", serif',             label: 'Suez One · עבה',       hebrew: true },
    { value: '"Secular One", sans-serif',     label: 'Secular One · מודרני', hebrew: true },
    { value: '"Varela Round", sans-serif',    label: 'Varela Round · רך',    hebrew: true },
    // Display / English
    { value: '"Bebas Neue", sans-serif',      label: 'Bebas Neue · BOLD',    hebrew: false },
    { value: '"Oswald", sans-serif',          label: 'Oswald · Tall',         hebrew: false },
    { value: '"Montserrat", sans-serif',      label: 'Montserrat',            hebrew: false },
    { value: '"Poppins", sans-serif',         label: 'Poppins',               hebrew: false },
    { value: '"Raleway", sans-serif',         label: 'Raleway',               hebrew: false },
    { value: '"Playfair Display", serif',     label: 'Playfair · Classic',    hebrew: false },
    // Script / handwriting
    { value: '"Dancing Script", cursive',     label: 'Dancing Script ✒️',     hebrew: false },
    { value: '"Great Vibes", cursive',        label: 'Great Vibes ✒️',        hebrew: false },
    { value: '"Pacifico", cursive',           label: 'Pacifico 🎉',           hebrew: false },
    { value: '"Lobster", cursive',            label: 'Lobster',               hebrew: false },
    { value: '"Caveat", cursive',             label: 'Caveat ✍️',             hebrew: false },
    // System fallbacks
    { value: 'Arial, sans-serif',             label: 'Arial',                 hebrew: true },
    { value: 'Georgia, serif',                label: 'Georgia',               hebrew: false },
    { value: 'Impact, sans-serif',            label: 'Impact',                hebrew: false },
    { value: '"Times New Roman", serif',      label: 'Times New Roman',       hebrew: false },
    { value: '"Courier New", monospace',      label: 'Courier (Mono)',        hebrew: false },
];

export default function InteractiveFrameEditor({
    frameStyle = {},
    frameColor = '#FFFFFF',
    previewImage = '/demos/girls/actress.jpg',
    onChange,
    initialElements = [],
}) {
    const { language } = useLanguage();
    const containerRef = useRef(null);
    const [elements, setElements] = useState(initialElements.length ? initialElements : []);
    const [selectedId, setSelectedId] = useState(null);
    const [dragging, setDragging] = useState(null);
    const [resizing, setResizing] = useState(null);

    // Polaroid layout — derive editor dimensions from selected frame's print ratio.
    const layout = useMemo(() => getPolaroidLayout(frameStyle?.id), [frameStyle?.id]);
    const EDITOR_WIDTH  = layout.orientation === 'landscape' || layout.outputWidth > layout.outputHeight ? 600 : 400;
    const EDITOR_HEIGHT = Math.round(EDITOR_WIDTH * (layout.outputHeight / layout.outputWidth));
    const scale = EDITOR_WIDTH / layout.outputWidth;

    // Polaroid card margins (in editor pixels)
    const sidePx   = layout.sideMargin   * scale;
    const topPx    = layout.topMargin    * scale;
    const bottomPx = layout.bottomMargin * scale;

    const isLight = isLightHex(frameColor);
    const defaultTextColor = isLight ? '#1a1a1a' : '#FFFFFF';

    // Load Google Fonts once
    useEffect(() => {
        if (!document.getElementById('gf-frame-editor')) {
            const link = document.createElement('link');
            link.id = 'gf-frame-editor';
            link.rel = 'stylesheet';
            link.href = GOOGLE_FONTS_URL;
            document.head.appendChild(link);
        }
    }, []);

    // Notify parent
    useEffect(() => {
        if (onChange) onChange({ elements });
    }, [elements]); // eslint-disable-line

    const selectedEl = elements.find((el) => el.id === selectedId);

    const updateElement = useCallback((id, updates) => {
        setElements((prev) =>
            prev.map((el) => (el.id === id ? { ...el, ...updates } : el))
        );
    }, []);

    const addText = () => {
        // Drop new text in the center of the Polaroid caption area
        const captionCenterY = EDITOR_HEIGHT - bottomPx / 2;
        const newEl = {
            id: Date.now(),
            type: 'text',
            content: language === 'he' ? 'שם האירוע' : 'Event Name',
            x: Math.round(EDITOR_WIDTH * 0.25),
            y: Math.round(captionCenterY - 14),
            fontSize: 26,
            color: defaultTextColor,
            fontFamily: language === 'he' ? '"Rubik", sans-serif' : '"Bebas Neue", sans-serif',
        };
        setElements((prev) => [...prev, newEl]);
        setSelectedId(newEl.id);
    };

    const addLogo = () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.onchange = (e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            const reader = new FileReader();
            reader.onload = (ev) => {
                // Drop logo in caption area, left side
                const captionTopY = EDITOR_HEIGHT - bottomPx;
                const newEl = {
                    id: Date.now(),
                    type: 'logo',
                    src: ev.target.result,
                    x: Math.round(sidePx + 8),
                    y: Math.round(captionTopY + 12),
                    width: 70,
                    height: 50,
                };
                setElements((prev) => [...prev, newEl]);
                setSelectedId(newEl.id);
            };
            reader.readAsDataURL(file);
        };
        input.click();
    };

    const deleteElement = (id) => {
        setElements((prev) => prev.filter((el) => el.id !== id));
        if (selectedId === id) setSelectedId(null);
    };

    // ── Drag / Resize handlers ──────────────────────────────
    const getClientXY = (e) => {
        const t = e.touches?.[0];
        return { cx: t ? t.clientX : e.clientX, cy: t ? t.clientY : e.clientY };
    };

    const startDrag = useCallback((e, el) => {
        e.stopPropagation();
        e.preventDefault();
        setSelectedId(el.id);
        const rect = containerRef.current?.getBoundingClientRect();
        if (!rect) return;
        const { cx, cy } = getClientXY(e);
        setDragging({ id: el.id, offsetX: cx - rect.left - el.x, offsetY: cy - rect.top - el.y });
    }, []);

    const startResize = useCallback((e, el) => {
        e.stopPropagation();
        e.preventDefault();
        const { cx, cy } = getClientXY(e);
        setResizing({
            id: el.id,
            type: el.type,
            startX: cx,
            startY: cy,
            startFontSize: el.fontSize || 22,
            startW: el.width || 80,
            startH: el.height || 50,
        });
    }, []);

    const onMove = useCallback((e) => {
        if (!dragging && !resizing) return;
        e.preventDefault();
        const rect = containerRef.current?.getBoundingClientRect();
        if (!rect) return;
        const { cx, cy } = getClientXY(e);

        if (dragging) {
            const newX = Math.max(0, Math.min(EDITOR_WIDTH - 20, cx - rect.left - dragging.offsetX));
            const newY = Math.max(0, Math.min(EDITOR_HEIGHT - 20, cy - rect.top - dragging.offsetY));
            updateElement(dragging.id, { x: Math.round(newX), y: Math.round(newY) });
        }

        if (resizing) {
            const dx = cx - resizing.startX;
            const dy = cy - resizing.startY;

            if (resizing.type === 'text') {
                // For text: use drag distance to scale fontSize (no width change = no wrapping)
                const delta = dx + dy; // combine horizontal + vertical drag
                const newFontSize = Math.round(Math.max(8, Math.min(120, resizing.startFontSize + delta * 0.3)));
                updateElement(resizing.id, { fontSize: newFontSize });
            } else {
                // For logo: resize width + height
                const newW = Math.max(20, Math.round(resizing.startW + dx));
                const newH = Math.max(10, Math.round(resizing.startH + dy));
                updateElement(resizing.id, { width: newW, height: newH });
            }
        }
    }, [dragging, resizing, updateElement]);

    const onUp = useCallback(() => {
        setDragging(null);
        setResizing(null);
    }, []);

    useEffect(() => {
        const opts = { passive: false };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        window.addEventListener('touchmove', onMove, opts);
        window.addEventListener('touchend', onUp);
        return () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
            window.removeEventListener('touchmove', onMove);
            window.removeEventListener('touchend', onUp);
        };
    }, [onMove, onUp]);

    return (
        <div className="space-y-4 select-none">
            {/* Toolbar */}
            <div className="flex items-center gap-2 flex-wrap">
                <Button type="button" variant="outline" size="sm" onClick={addText}
                    className="border-violet-500/50 text-violet-300 hover:bg-violet-600/20 gap-1.5">
                    <Plus className="w-3.5 h-3.5" /><Type className="w-3.5 h-3.5" />
                    {language === 'he' ? 'הוסף כיתוב' : 'Add Text'}
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={addLogo}
                    className="border-pink-500/50 text-pink-300 hover:bg-pink-600/20 gap-1.5">
                    <Plus className="w-3.5 h-3.5" /><ImageIcon className="w-3.5 h-3.5" />
                    {language === 'he' ? 'הוסף לוגו' : 'Add Logo'}
                </Button>
                {selectedEl && (
                    <Button type="button" variant="outline" size="sm" onClick={() => deleteElement(selectedId)}
                        className="border-red-500/50 text-red-400 hover:bg-red-600/20 gap-1.5 ml-auto">
                        <Trash2 className="w-3.5 h-3.5" />
                        {language === 'he' ? 'מחק' : 'Delete'}
                    </Button>
                )}
            </div>

            {/* Canvas — actual Polaroid card */}
            <div
                ref={containerRef}
                className="relative mx-auto overflow-hidden shadow-2xl"
                style={{
                    width: EDITOR_WIDTH,
                    height: EDITOR_HEIGHT,
                    background: frameColor,
                    borderRadius: 6,
                    cursor: dragging ? 'grabbing' : 'default',
                    touchAction: 'none',
                }}
                onClick={() => setSelectedId(null)}
            >
                {/* Photo area (thin top/sides, thick bottom for caption) */}
                <div
                    className="absolute overflow-hidden bg-slate-900"
                    style={{
                        top: topPx,
                        left: sidePx,
                        right: sidePx,
                        bottom: bottomPx,
                        boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.08)',
                    }}
                >
                    <img src={previewImage} alt="Preview" className="w-full h-full object-cover" draggable={false} />
                </div>

                {/* Caption area visual hint when empty */}
                {elements.length === 0 && (
                    <div
                        className="absolute pointer-events-none flex items-center justify-center"
                        style={{
                            left: sidePx,
                            right: sidePx,
                            bottom: 0,
                            height: bottomPx,
                            border: '2px dashed rgba(0,0,0,0.15)',
                            borderRadius: 4,
                            margin: 10,
                        }}
                    >
                        <span style={{ color: isLight ? '#9ca3af' : '#cbd5e1', fontSize: 13, fontStyle: 'italic' }}>
                            {language === 'he' ? '— הוסיפו כיתוב או לוגו —' : '— Add caption or logo —'}
                        </span>
                    </div>
                )}

                {/* Elements */}
                {elements.map((el) => {
                    const isSel = el.id === selectedId;

                    if (el.type === 'text') {
                        return (
                            <div
                                key={el.id}
                                style={{
                                    position: 'absolute',
                                    left: el.x,
                                    top: el.y,
                                    cursor: dragging?.id === el.id ? 'grabbing' : 'grab',
                                    zIndex: isSel ? 20 : 10,
                                    // NO max-width or width limit → prevents wrapping
                                }}
                                onMouseDown={(e) => startDrag(e, el)}
                                onTouchStart={(e) => startDrag(e, el)}
                                onClick={(e) => { e.stopPropagation(); setSelectedId(el.id); }}
                            >
                                <div
                                    style={{
                                        fontSize: el.fontSize,
                                        color: el.color,
                                        fontFamily: el.fontFamily || '"Rubik", sans-serif',
                                        fontWeight: 700,
                                        // soft shadow only when text is on photo (not on white polaroid bg)
                                        textShadow: isLight ? 'none' : '0 2px 8px rgba(0,0,0,0.7)',
                                        whiteSpace: 'nowrap',
                                        lineHeight: 1.1,
                                        padding: '2px 4px',
                                        outline: isSel ? '2px solid rgba(139,92,246,0.8)' : 'none',
                                        outlineOffset: '2px',
                                        borderRadius: 3,
                                    }}
                                >
                                    {el.content}
                                </div>

                                {/* Resize handle (bottom-right) */}
                                {isSel && (
                                    <div
                                        title={language === 'he' ? 'גרור לשינוי גודל' : 'Drag to resize'}
                                        style={{
                                            position: 'absolute',
                                            bottom: -6,
                                            right: -6,
                                            width: 14,
                                            height: 14,
                                            background: '#8b5cf6',
                                            border: '2px solid white',
                                            borderRadius: 3,
                                            cursor: 'se-resize',
                                            boxShadow: '0 2px 6px rgba(0,0,0,0.4)',
                                        }}
                                        onMouseDown={(e) => startResize(e, el)}
                                        onTouchStart={(e) => startResize(e, el)}
                                    />
                                )}
                            </div>
                        );
                    }

                    if (el.type === 'logo') {
                        return (
                            <div
                                key={el.id}
                                style={{
                                    position: 'absolute',
                                    left: el.x,
                                    top: el.y,
                                    width: el.width,
                                    height: el.height,
                                    cursor: dragging?.id === el.id ? 'grabbing' : 'grab',
                                    zIndex: isSel ? 20 : 10,
                                }}
                                onMouseDown={(e) => startDrag(e, el)}
                                onTouchStart={(e) => startDrag(e, el)}
                                onClick={(e) => { e.stopPropagation(); setSelectedId(el.id); }}
                            >
                                <img
                                    src={el.src}
                                    alt="Logo"
                                    draggable={false}
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'contain',
                                        outline: isSel ? '2px solid rgba(236,72,153,0.8)' : 'none',
                                        outlineOffset: '2px',
                                        borderRadius: 3,
                                    }}
                                />
                                {isSel && (
                                    <div
                                        title={language === 'he' ? 'גרור לשינוי גודל' : 'Drag to resize'}
                                        style={{
                                            position: 'absolute',
                                            bottom: -6,
                                            right: -6,
                                            width: 14,
                                            height: 14,
                                            background: '#ec4899',
                                            border: '2px solid white',
                                            borderRadius: 3,
                                            cursor: 'se-resize',
                                            boxShadow: '0 2px 6px rgba(0,0,0,0.4)',
                                        }}
                                        onMouseDown={(e) => startResize(e, el)}
                                        onTouchStart={(e) => startResize(e, el)}
                                    />
                                )}
                            </div>
                        );
                    }

                    return null;
                })}

            </div>

            {/* Helper tips */}
            <div className="flex gap-4 text-xs text-slate-500">
                <span className="flex items-center gap-1"><Move className="w-3 h-3" />{language === 'he' ? 'גרור להזזה' : 'Drag to move'}</span>
                <span className="flex items-center gap-1"><Maximize2 className="w-3 h-3" />{language === 'he' ? 'גרור פינה לשינוי גודל' : 'Drag corner to resize'}</span>
            </div>

            {/* Properties panel */}
            {selectedEl && (
                <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-4 space-y-4">
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                        {selectedEl.type === 'text'
                            ? (language === 'he' ? '✏️ ערוך כיתוב' : '✏️ Edit Text')
                            : (language === 'he' ? '🖼️ ערוך לוגו' : '🖼️ Edit Logo')}
                    </p>

                    {selectedEl.type === 'text' && (
                        <div className="space-y-3">
                            {/* Text input */}
                            <Input
                                value={selectedEl.content}
                                onChange={(e) => updateElement(selectedId, { content: e.target.value })}
                                className="bg-slate-900 border-slate-700 text-white"
                                placeholder={language === 'he' ? 'הקלד טקסט...' : 'Type text...'}
                            />

                            <div className="grid grid-cols-2 gap-3">
                                {/* Font */}
                                <div>
                                    <label className="text-xs text-slate-500 mb-1 block">{language === 'he' ? 'גופן' : 'Font'}</label>
                                    <select
                                        value={selectedEl.fontFamily || 'Rubik'}
                                        onChange={(e) => updateElement(selectedId, { fontFamily: e.target.value })}
                                        className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-2 text-white text-sm"
                                    >
                                        {FONTS.map((f) => (
                                            <option key={f.value} value={f.value}>{f.label}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Color */}
                                <div>
                                    <label className="text-xs text-slate-500 mb-1 block">{language === 'he' ? 'צבע' : 'Color'}</label>
                                    <div className="flex gap-2 items-center">
                                        <input
                                            type="color"
                                            value={selectedEl.color}
                                            onChange={(e) => updateElement(selectedId, { color: e.target.value })}
                                            className="w-10 h-10 rounded cursor-pointer border border-slate-700 bg-transparent"
                                        />
                                        {/* Quick color presets */}
                                        <div className="flex gap-1 flex-wrap">
                                            {['#FFFFFF', '#000000', '#FFD700', '#FF6B6B', '#4ECDC4', '#C7F464'].map((c) => (
                                                <button
                                                    key={c}
                                                    type="button"
                                                    onClick={() => updateElement(selectedId, { color: c })}
                                                    className="w-6 h-6 rounded-full border-2 border-slate-700 hover:scale-110 transition-transform"
                                                    style={{ background: c }}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Font size manual input */}
                            <div className="flex items-center gap-3">
                                <label className="text-xs text-slate-500 shrink-0">
                                    {language === 'he' ? 'גודל:' : 'Size:'}
                                </label>
                                <input
                                    type="range"
                                    min={8}
                                    max={120}
                                    value={selectedEl.fontSize}
                                    onChange={(e) => updateElement(selectedId, { fontSize: Number(e.target.value) })}
                                    className="flex-1 accent-violet-500"
                                />
                                <span className="text-sm text-slate-300 w-10 text-right">{selectedEl.fontSize}px</span>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function isLightHex(hex) {
    if (!hex || typeof hex !== 'string') return true;
    const h = hex.replace('#', '');
    if (h.length !== 6) return true;
    const r = parseInt(h.slice(0, 2), 16) / 255;
    const g = parseInt(h.slice(2, 4), 16) / 255;
    const b = parseInt(h.slice(4, 6), 16) / 255;
    return (0.299 * r + 0.587 * g + 0.114 * b) > 0.6;
}
